import { useTranslation } from "react-i18next";
import {
	FrontendOrderItem,
	FrontendProduct,
	FrontendSampleSpecification,
	FrontendSampleTestBatch,
	FrontendUserInfo,
	FrontendUserLicense,
} from "node-library/dist/portal";
import ItemPreviewTable from "./itemPreviewTable";
import {
	CustomCheckbox,
	CustomNumberInput,
	CustomNumberInputDropdown,
	CustomPriceInput,
	RowDropdown,
	StandardRadioGroup,
} from "../../../common-components/inputs";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Autocomplete, Card, Divider, TextField } from "@mui/material";
import {
	SkyBlueButton,
	CustomIconButton,
	DropdownButton,
	BlueButton,
	FlexDropdownButton,
} from "../../../common-components/button";
import {
	AddIcon,
	AlertIcon,
	SaveIcon,
} from "../../../common-components/svgIcons";
import ClearIcon from "@mui/icons-material/Clear";
import {
	NeutralColor,
	PrimaryColor,
	SemanticColor,
} from "node-library/dist/colors";
import React, { useEffect, useRef, useState } from "react";
import DiscardOrderConfirmModal from "../../../common-components/modals/discardOrderConfirmModal";
import KeyboardArrowLeftIconRounded from "@mui/icons-material/KeyboardArrowLeftRounded";
import NewOrderSkeletonUI from "./skeletonUI/newOrderSkeleton";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import {
	DiscountUnit,
	Severity,
	TaxOptions,
} from "node-library/dist/constants";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import CurrencySelector from "../../../common-components/currencySelector";
import { HANDLING_DEFAULT_DESCRIPTION } from "node-library/dist/description";
import {
	calculateDiscount,
	calculateItemPrice,
	formatPrice,
	getTaxOptionTranslationText,
} from "../../../helpers/sampleHelper";
import { YellowWarnIcon } from "../../../common-components/toast/warnIcons";

interface Props {
	licenseLoading: boolean;
	isInAdminView?: boolean;

	goBackToOrderDetailsPage?: () => void;
	goBackToClientDetail: () => void;
	itemList: Array<FrontendOrderItem>;
	deleteItem: (itemId: string) => void;
	deleteHandlingFee: () => void;
	deleteShippingFee: () => void;
	goToUpdateItem: (item: FrontendOrderItem) => void;
	goToAddNewItem: () => void;
	goToAddShippingHandling: () => void;
	goToReviewOrder: () => void;
	discardOrder: () => Promise<void>;
	saveOrderAndEditLater: () => Promise<void>;
	quoteNumber: string;
	handlingFee: number;
	handlingDescription: string;
	shippingFee: number;
	shippingDescription: string;
	fullServiceShippingSolution: boolean;
	discount: number;
	setDiscount: React.Dispatch<React.SetStateAction<number>>;
	discountUnit: DiscountUnit;
	setDiscountUnit: React.Dispatch<React.SetStateAction<DiscountUnit>>;
	tax: number;
	setTax: React.Dispatch<React.SetStateAction<number>>;
	taxOption: TaxOptions;
	setTaxOption: React.Dispatch<React.SetStateAction<TaxOptions>>;
	currency: string; // or a more specific type if you have one
	setCurrency: React.Dispatch<React.SetStateAction<string>>;
	total: number;
	setTotal: React.Dispatch<React.SetStateAction<number>>;
	subtotal: number;
	setSubtotal: React.Dispatch<React.SetStateAction<number>>;
	itemTotal: number;
	setItemTotal: React.Dispatch<React.SetStateAction<number>>;
	setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function OrderAddItems(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		itemList,
		deleteItem,
		goToUpdateItem,
		goToAddNewItem,
		goToAddShippingHandling,
		goToReviewOrder,
		discardOrder,
		saveOrderAndEditLater,
		handlingFee,
		shippingFee,
		discount,
		setDiscount,
		discountUnit,
		setDiscountUnit,
		tax,
		setTax,
		taxOption,
		setTaxOption,
		currency,
		setCurrency,
		total,
		setTotal,
		subtotal,
		setSubtotal,
		itemTotal,
		setItemTotal,
		quoteNumber,
	} = props;

	//confirm modals
	const [discardOrderConfirmOpen, setDiscardOrderConfirmOpen] =
		useState<boolean>(false);

	const [taxErrorMsg, setTaxErrorMsg] = useState<string>("");

	const [currencyOption, setCurrencyOption] = useState<"CAD" | "USD" | "Other">(
		props.currency !== "CAD" && props.currency !== "USD"
			? "Other"
			: props.currency
	);

	const currencyOptions = [
		{
			name: "CAD",
			value: "CAD",
			key: "CAD",
			symbol: "CA$",
		},
		{
			name: "USD",
			value: "USD",
			key: "USD",
			symbol: "$",
		},
		{
			name: "Other",
			value: "Other",
			key: "Other",
		},
	];

	const taxOptions = Object.values(TaxOptions).map((value) => ({
		name: getTaxOptionTranslationText(value, translation),
		value: value,
		key: value,
	}));

	function handleSave() {
		if (itemList.length === 0) {
			openSnackbar(
				translation("ItemListEmpty"),
				Severity.WARN,
				translation("ItemListEmptyDescription"),
				<YellowWarnIcon />
			);
			return;
		}
		if (taxOption === TaxOptions.OTHER && tax == 0) {
			setTaxErrorMsg(translation("MissingInformation"));
			openSnackbar(
				translation("MissingInformation"),
				Severity.WARN,
				translation("MissingInformationError"),
				<YellowWarnIcon />
			);
			return;
		}

		goToReviewOrder();
	}

	useEffect(() => {
		const total: number = itemList.reduce(
			(sum, item) => sum + calculateItemPrice(item),
			0
		);
		setItemTotal(total);
	}, [itemList]);

	useEffect(() => {
		const total: number =
			itemTotal -
			calculateDiscount(itemTotal, discount, discountUnit) +
			shippingFee +
			handlingFee;
		setSubtotal(total);
	}, [itemTotal, handlingFee, shippingFee, discount, discountUnit]);

	useEffect(() => {
		const total: number = subtotal + tax;
		setTotal(total);
	}, [subtotal, tax]);

	const addItemToOrderButton: React.ReactNode = (
		<div style={{ flex: "2" }}>
			<FlexDropdownButton
				buttonColor="blue"
				buttonClassName=" add-item-to-order-button"
				id={"add-item-to-order-button"}
				menuOptions={[
					{
						key: "add-instrument-and-service",
						onClick: () => {
							goToAddNewItem();
						},
						children: <span>{translation("AnalyticalInstrumentService")}</span>,
					},
					{
						key: "shipping-handling-fee",
						onClick: () => {
							goToAddShippingHandling();
						},
						children: <span>{translation("ShippingHandlingFee")}</span>,
					},
				]}
				onClick={(e) => {}}
				isFlexButton={true}
			>
				<div
					className="order-add-items-collapsible-text"
					style={{
						display: "flex",
						gap: "10px",
						justifyContent: "space-between",
					}}
				>
					<AddIcon></AddIcon>
					<span>{translation("AddItemToOrder")}</span>
				</div>
			</FlexDropdownButton>
		</div>
	);

	return (
		<>
			{props.licenseLoading ? (
				<NewOrderSkeletonUI />
			) : (
				<div style={{ margin: "0 auto" }}>
					{
						<div className="page-content-padding">
							<div
								style={{
									display: "flex",
									justifyContent: "space-between",
									paddingBottom: "16px",
								}}
							>
								<div
									className="typography-heading-02 typography-bold"
									style={{ display: "flex", gap: "5px", alignItems: "center" }}
								>
									<CustomIconButton
										id="back-to-order-client-details-button"
										size="small"
										onClick={() => {
											props.goBackToClientDetail();
										}}
										icon={<KeyboardArrowLeftIconRounded />}
									></CustomIconButton>
									{`${translation("Quote")}: ${quoteNumber}`}
								</div>
								<DropdownButton
									buttonColor="white"
									buttonClassName="new-order-collapible-button"
									id={"new-orders-more-actions"}
									menuOptions={[
										...(itemList.length === 0 || props.isInAdminView
											? []
											: [
													{
														key: "submit-later",
														onClick: () => {
															saveOrderAndEditLater();
														},
														children: (
															<div className="icon-dropdown-menu-item">
																<SaveIcon />
																{translation("SaveAndSubmitLater")}
															</div>
														),
													},
											  ]),
										{
											key: "discard-order",
											onClick: () => {
												setDiscardOrderConfirmOpen(true);
											},
											children: (
												<div className="icon-dropdown-menu-item">
													<ClearIcon sx={{ fontSize: "20px" }} />
													{translation("Discardorder")}
												</div>
											),
										},
									]}
									onClick={(e) => {}}
								>
									<span className="new-order-collapsible-text">
										{translation("Action")}
									</span>
								</DropdownButton>
							</div>
							<div style={{ display: "flex", gap: "24px", flexWrap: "wrap" }}>
								<div style={{ flex: "3 3", minWidth: "300px" }}>
									{addItemToOrderButton}
									{itemList.length === 0 &&
									handlingFee === 0 &&
									shippingFee === 0 &&
									!props.fullServiceShippingSolution ? (
										<div
											style={{
												marginTop: "32px",
												marginLeft: "16px",
												color: NeutralColor.NEUTRAL_GREY_200,
											}}
										>
											<div className="typography-heading-04">
												{translation("NoItemInOrder")}
											</div>
										</div>
									) : (
										<ItemPreviewTable
											itemList={itemList}
											deleteItem={deleteItem}
											goToUpdateItem={goToUpdateItem}
											currency={currency}
											shippingFee={shippingFee}
											handlingFee={handlingFee}
											handlingDescription={props.handlingDescription}
											shippingDescription={props.shippingDescription}
											fullServiceShippingSolution={
												props.fullServiceShippingSolution
											}
											deleteHandlingFee={props.deleteHandlingFee}
											deleteShippingFee={props.deleteShippingFee}
											goToUpdateHandlingShippingFee={goToAddShippingHandling}
										/>
									)}
								</div>
								<div
									style={{
										display: "flex",
										flexDirection: "column",
										flex: "1",
										gap: "16px",
									}}
								>
									<Card
										style={{
											display: "grid",
											padding: "16px 18px",
											minWidth: "150px",
											height: "fit-content",
										}}
									>
										<div
											className="typography-heading-04 typography-bold"
											style={{ paddingBottom: "16px" }}
										>
											{translation("PriceSettings")}
										</div>
										<div
											style={{
												display: "grid",
												gap: "12px",
												margin: "8px 0px",
											}}
										>
											<div>{translation("Currency")}</div>
											<StandardRadioGroup
												options={currencyOptions}
												selectedOption={currencyOption}
												onChange={(e) => {
													if (e.target.value !== "Other") {
														setCurrency(e.target.value);
													}
													setCurrencyOption(e.target.value);
												}}
												className="new-order-currency-radio-group"
											/>
											{currencyOption === "Other" && (
												<CurrencySelector
													currencyDropdownId="new-order-currency"
													selectedDisplayCurrency={currency}
													setSelectedDisplayCurrency={setCurrency}
												/>
											)}
										</div>
										<div
											style={{
												display: "grid",
												gap: "12px",
												margin: "8px 0px",
											}}
										>
											<div>{translation("Taxes")}</div>
											<StandardRadioGroup
												options={taxOptions}
												selectedOption={taxOption}
												onChange={(e) => {
													if (e.target.value !== "Other") {
														setTax(0);
													}
													setTaxOption(e.target.value);
												}}
												className="new-order-tax-radio-group"
											/>
											{taxOption === TaxOptions.OTHER && (
												<CustomPriceInput
													id="new-order-tax-input"
													value={tax}
													setValue={(value) => {
														setTax(value);
													}}
													placeholder={"0"}
													required={taxOption === TaxOptions.OTHER}
													errorMsg={taxErrorMsg}
													setErrorMsg={setTaxErrorMsg}
													currency={currency}
												/>
											)}
										</div>
									</Card>
									<Card
										style={{
											display: "grid",
											padding: "16px 18px",
											minWidth: "150px",
											height: "fit-content",
										}}
									>
										<div
											className="typography-heading-04 typography-bold"
											style={{ paddingBottom: "16px" }}
										>
											{translation("PriceSummary")}
										</div>
										<div className="price-summary-content">
											<div className="typography-body-01 price-summary-row">
												<span>{`${translation("Item")} x ${
													itemList.length
												}`}</span>
												<span>
													{itemList.length > 0
														? `${formatPrice(itemTotal, currency)}`
														: "--"}
												</span>
											</div>
											<div className="typography-body-01 price-summary-row">
												<span>{`${translation("Discount")}${
													discountUnit === DiscountUnit.PERCENTAGE
														? ` (${discount}%)`
														: ""
												}`}</span>
												<span>
													{discount > 0
														? `- ${formatPrice(
																calculateDiscount(
																	itemTotal,
																	discount,
																	discountUnit
																),
																currency
														  )}`
														: "--"}
												</span>
											</div>
											<div className="typography-body-01 price-summary-row">
												<span>{`${translation("Handling")}`}</span>
												<span>
													{handlingFee > 0
														? `${formatPrice(handlingFee, currency)}`
														: "--"}
												</span>
											</div>
											<div className="typography-body-01 price-summary-row">
												<span>{`${translation("Shipping")}`}</span>
												<span>
													{shippingFee > 0
														? `${formatPrice(shippingFee, currency)}`
														: "--"}
												</span>
											</div>
											<div className="typography-body-01 price-summary-row">
												<span>{`${translation("Subtotal")}`}</span>
												<span>
													{subtotal > 0
														? `${formatPrice(subtotal, currency)}`
														: "--"}
												</span>
											</div>
											<div className="typography-body-01 price-summary-row">
												<span>{`${translation("Tax")}`}</span>
												<span>
													{taxOption === TaxOptions.EXCLUDED
														? translation("EXCLUDED")
														: tax > 0
														? `${formatPrice(tax, currency)}`
														: "--"}
												</span>
											</div>
											<div
												className="typography-body-01 typography-bold price-summary-row"
												style={{ color: "#121212" }}
											>
												<span>{`${translation("Total")}`}</span>
												<span>
													{total > 0 ? `${formatPrice(total, currency)}` : "--"}
												</span>
											</div>
											<BlueButton
												key="go-to-review-order-button"
												id="go-to-review-order-button"
												children={translation("ReviewOrder")}
												size="flex"
												onClick={() => {
													handleSave();
												}}
											></BlueButton>
										</div>
										{/* <Divider
											className="new-order-price-summary-divider"
											orientation="horizontal"
											sx={{ my: 1, borderStyle: "dashed" }}
											flexItem
										/> */}
									</Card>
									<Card
										style={{
											padding: "16px 18px",
											minWidth: "150px",
											height: "fit-content",
										}}
									>
										<CustomNumberInputDropdown
											id="new-order-dicount-input"
											value={discount}
											setValue={(value) => {
												setDiscount(value);
											}}
											label={translation("ApplyDiscount")}
											placeholder={"0"}
											selectId="discount-unit-selection"
											selectValue={discountUnit}
											setSelectValue={(value) => {
												setDiscountUnit(value);
												if (
													value === DiscountUnit.NUMBER &&
													discount > subtotal
												) {
													setDiscount(subtotal);
												}
												if (
													value === DiscountUnit.PERCENTAGE &&
													discount > 100
												) {
													setDiscount(100);
												}
											}}
											options={Object.values(DiscountUnit).map((unit) => ({
												value: unit,
												title: unit,
											}))}
											maxValue={
												discountUnit === DiscountUnit.NUMBER ? itemTotal : 100
											}
										/>
									</Card>
								</div>
							</div>
						</div>
					}
					<DiscardOrderConfirmModal
						open={discardOrderConfirmOpen}
						setOpen={setDiscardOrderConfirmOpen}
						proceedAction={() => {
							discardOrder();
						}}
					></DiscardOrderConfirmModal>
				</div>
			)}
		</>
	);
}
